import React, {useEffect, useState} from 'react';

import {Modal} from 'components';
import ComponentForm from './ComponentForm';
import ComponentList from './ComponentList';
import {Component} from '../types';
import {Bit, ComponentType} from 'api/types/wisbitTypes';

import i18n from 'i18next';
import {componentName} from '../utils';
import {useDispatch} from 'react-redux';
import {
  createBitComponent,
  deleteBitComponent,
  updateBitComponent,
} from '../redux/actions';
import {saveWisbit} from '../redux/thunks';
import {OPERATE_TYPES} from 'api/constants';
import {FIELDS} from '../constants';

interface AddComponentModalProps {
  bitIndex: number;
  bitData: Bit;
  visible: boolean;
  onClose: () => void;
  newType?: ComponentType;
  disabledInteractive?: boolean;
  disabled?: boolean;
  component?: Component;
}

function AddComponentModal(props: AddComponentModalProps) {
  // Local state
  const [selectedComponent, setSelectedComponent] = useState<ComponentType>();

  // Redux
  const dispatch = useDispatch();

  // Handles creation of a new component.
  const handleAddComponent = (newComponent: Partial<Component>) => {
    dispatch(createBitComponent(props.bitData.id, newComponent));

    dispatch(
      saveWisbit({
        op: OPERATE_TYPES.ADD,
        field: FIELDS.BITS,
        to: {
          bitIndex: props.bitIndex,
        },
      })
    );

    props.onClose();
  };

  // Handles save operation on the existing component.
  const handleSaveComponent = (selectedComponent: Component) => {
    dispatch(updateBitComponent(props.bitData.id, selectedComponent));

    const componentIndex = props.bitData.components?.findIndex(
      ({id}) => id === selectedComponent.id
    );

    dispatch(
      saveWisbit({
        op: OPERATE_TYPES.REPLACE,
        field: FIELDS.BITS,
        to: {
          bitIndex: props.bitIndex,
          componentIndex,
        },
      })
    );

    props.onClose();
  };

  // Handles deletion of the Bit's particular component identified by id.
  const handleDeleteComponent = (selectedId: string) => {
    dispatch(deleteBitComponent(props.bitData.id, selectedId));

    const componentIndex = props.bitData.components?.findIndex(
      ({id}) => id === selectedId
    );

    dispatch(
      saveWisbit({
        op: OPERATE_TYPES.REMOVE,
        field: FIELDS.BITS,
        to: {
          bitIndex: props.bitIndex,
          componentIndex,
        },
      })
    );

    props.onClose();
  };

  const handleGoBack = () => {
    setSelectedComponent(undefined);
  };

  // Effects

  useEffect(() => {
    if (!props.visible) {
      // If the view became invisible set selected component to
      // be undefined to prepare for the next potential selection.
      setSelectedComponent(undefined);
    } else if (props.component) {
      // If the modal is visible and component was provided (editing
      // existing component), then we want to open the editor for that
      // component by selecting which type of component we want to edit.
      setSelectedComponent(props.component.type);
    }
  }, [props.visible]);

  return (
    <Modal show={props.visible} onClickOutside={props.onClose}>
      {/* Back button. Only render when component is selected. */}
      {selectedComponent && !props.component && (
        <div
          className="text-dodger-blue cursor-pointer select-none pb-2"
          onClick={handleGoBack}
        >{`< ${i18n.t('common.back')}`}</div>
      )}

      {/* Header */}
      <h4 className="text-2xl font-bold pb-4">
        {!selectedComponent
          ? i18n.t('wisbitPage.selectComponentType.title')
          : componentName(selectedComponent)}
      </h4>

      {/* If component was not selected show the component list. */}
      {/* Otherwise show the new selected component form. */}
      {!selectedComponent ? (
        <ComponentList
          onCancel={props.onClose}
          onSelectComponent={setSelectedComponent}
          disabledInteractive={props.disabledInteractive}
        />
      ) : (
        <ComponentForm
          component={props.component}
          type={selectedComponent}
          disabled={props.disabled}
          onCancel={props.onClose}
          onGoBack={() => setSelectedComponent(undefined)}
          onConfirm={handleAddComponent}
          onEdit={handleSaveComponent}
          onDelete={handleDeleteComponent}
        />
      )}
    </Modal>
  );
}

export default AddComponentModal;
